import { view } from 'ramda';
import { differenceInDays } from 'date-fns/esm';
import { DISPLAY, CLICK } from '@updater-core/lib/tracker';
import {
  idLens,
  nameLens,
  codeLens,
  promotionCodeLens,
  promotionPlacementsLens,
} from '../../data';
import { formatPromotionForEventStream } from '../../eventstream';
import { forEach } from 'lodash-es';

export function filterPromosByCodes(
  {
    eligiblePromotions: { eligiblePromotions = [] } = {},
    claimedPromotions: { claimedPromotions = [] } = {},
  },
  codes = []
) {
  const codeFilter = (promo) => codes.includes(view(promotionCodeLens, promo));
  let ePromotions = eligiblePromotions.filter(codeFilter);
  const cPromotions = claimedPromotions.filter(
    (promo) =>
      codeFilter(promo) &&
      differenceInDays(new Date(promo.claimedAt), Date.now()) === 0
  );

  const promos = [...ePromotions, ...cPromotions];
  if (!promos && !promos.length) {
    return undefined;
  }

  return promos;
}

export function filterPromosByPlacements(
  {
    eligiblePromotions: { eligiblePromotions = [] } = {},
    claimedPromotions: { claimedPromotions = [] } = {},
  },
  placement,
  promotionsType = ''
) {
  let promos = [];
  const placementFilter = (promo) =>
    view(promotionPlacementsLens, promo).includes(placement);

  let ePromotions = eligiblePromotions.filter(placementFilter);
  const cPromotions = claimedPromotions.filter(
    (promo) =>
      placementFilter(promo) &&
      differenceInDays(new Date(promo.claimedAt), Date.now()) === 0
  );

  if (promotionsType === 'eligible') {
    promos = ePromotions;
  } else if (promotionsType === 'claimed') {
    promos = cPromotions;
  } else {
    promos = [...ePromotions, ...cPromotions];
  }

  if (!promos && !promos.length) {
    return undefined;
  }

  return promos;
}

export function trackDisplay(track, promotions = []) {
  forEach(promotions, (promotion) => {
    if (promotion.__typename === 'EligiblePromotion') {
      promotion.validFor.forEach((entity) => {
        const details = {
          promotionId: view(idLens, promotion.promotion),
          entityId: view(idLens, entity),
          entityName: view(nameLens, entity),
          code: view(codeLens, promotion.promotion),
          promotionCode: view(codeLens, promotion.promotion),
          promotion: formatPromotionForEventStream(promotion.promotion, entity),
          source: 'forward_mail',
        };

        const eoViewedEvent = {
          verb: DISPLAY,
          domain: 'promotions',
          object: 'exclusive_offer',
          segmentEventName: 'Viewed Promotions Exclusive Offer',
          details,
        };

        track(eoViewedEvent);
      });
    } else {
      track({
        verb: DISPLAY,
        domain: 'promotions',
        object: 'exclusive_offer',
        segmentEventName: 'Viewed Promotions Claimed Offer',
        details: {
          promotionId: view(idLens, promotion.promotion),
          entityId: view(idLens, promotion.claimedFor),
          entityName: view(nameLens, promotion.claimedFor),
          code: view(codeLens, promotion.promotion),
          promotionCode: view(codeLens, promotion.promotion),
          source: 'forward_mail',
        },
      });
    }
  });
}

export function trackRedeem(track, promotion) {
  const details = {
    promotionId: promotion.promotionId,
    claimedId: promotion.claimedId,
    link: promotion.link,
    entityId: promotion.entityId,
    entityName: promotion.entityName,
    code: promotion.code,
    promotionCode: promotion.promotionCode,
    source: 'forward_mail',
  };

  const redeemEvent = {
    verb: CLICK,
    domain: 'promotions',
    object: 'redeem',
    segmentEventName: 'Clicked Promotions Redeem',
    details,
  };

  const linkOutEvent = {
    verb: CLICK,
    domain: 'promotions',
    object: 'link_out',
    segmentEventName: 'Clicked Promotions Link Out',
    details,
  };

  track(redeemEvent);
  track(linkOutEvent);
}

export function trackCopyToClipboard(track) {
  track({
    verb: CLICK,
    domain: 'promotions',
    object: 'copy_to_clipboard',
    details: {
      source: 'forward_mail',
    },
  });
}

export const couponSvg = `
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="30">
  <g fill="#39B3CA">
    <path d="M23.362.747a2.077 2.077 0 0 0-1.52-.611l-7.724.312c-.455.02-.885.21-1.208.53L.652 13.234a1.778 1.778 0 0 0-.516 1.344c.014.52.234 1.013.61 1.371l7.412 7.412a2.05 2.05 0 0 0 1.426.624c.48.01.943-.182 1.276-.529l12.244-12.244c.335-.31.535-.74.557-1.195l.312-7.737a2.05 2.05 0 0 0-.611-1.534zM9.855 22.56l-.054.027h-.367l-.095-.04h-.027l-.095-.055h-.013l-.095-.095-7.412-7.425a.706.706 0 0 1-.217-.435v-.081a.462.462 0 0 1 .122-.271L13.873 1.928h.014a.462.462 0 0 1 .299-.123l7.71-.312h.014a.57.57 0 0 1 .366.109h.014l.04.027h.014l.027.027.027.027c.13.142.207.324.217.516l-.312 7.724a.394.394 0 0 1-.122.299L9.923 22.507a.136.136 0 0 1-.068.054z"/>
    <path d="M20.783 16.208a.638.638 0 0 0-.516-.108.665.665 0 0 0-.434.285l-8.308 11.932a.42.42 0 0 1-.299.176.679.679 0 0 1-.502-.122l-8.647-5.973a.706.706 0 0 1-.285-.448.394.394 0 0 1 .054-.326l.733-1.072a.665.665 0 0 0-.163-.937.652.652 0 0 0-.516-.108.665.665 0 0 0-.434.285l-.733 1.045a1.765 1.765 0 0 0-.285 1.344c.096.54.404 1.02.855 1.33l8.634 6a2.036 2.036 0 0 0 1.52.353c.474-.08.895-.35 1.167-.746l8.322-11.96a.679.679 0 0 0-.163-.95zm-2.579-12.38a1.928 1.928 0 1 0 1.927 1.928 1.941 1.941 0 0 0-1.927-1.928zm0 1.371a.584.584 0 1 1-.584.584.557.557 0 0 1 .584-.584z"/>
  </g>
</svg>`;
